/**
 * deposits.ts — one contract's deposits, read once for the three surfaces that need them.
 *
 * THE SETUP PAGE USED TO READ THE SAME DEPOSITS THREE WAYS. `['deposit-payments']` fed the
 * financials card (amount + refunds), the signing board's `['agreement-board-subjects']` fetched
 * them again for its DEPOSIT slots (amount + the legacy agreement row), and
 * `DepositAgreementBlock` needed a third shape entirely (the depositor's name and phone, which
 * the deposit agreement prints). Three reads of one table, none of which invalidated the others:
 * linking a deposit from setup updated the card and left the signing board showing no deposit
 * agreement until a hard refresh.
 *
 * ONE SUPERSET ROW REPLACES ALL THREE. It carries the transaction, the refunds, the legacy
 * agreement arc and the driver's identity, and each caller reads the part it needs. The key sits
 * under the `contract-deposits` PREFIX so the existing `useDepositCacheInvalidator` clears it for
 * free — and the deposits page's paginated key (`['contract-deposits', page, …]`, a NUMBER in the
 * second position) is untouched, because this one puts a string there.
 *
 * THE DEPOSITOR'S IDENTITY IS THE DRIVER'S PROFILE, NOT A TYPED NAME. `agreement_create` discards
 * typed signer parameters whenever the deposit's contract has a driver with an account and
 * resolves `driver.full_name` / `driver.phone` through COALESCE(profile, external) — profile
 * first. So the block mounted here must show the same person the document will print, which is
 * why the read reaches `contracts → drivers → profiles`. PostgREST returns that embed as an ARRAY
 * (the FK to `profiles` is not detected as one-to-one), and `depositShapes` is the one place that
 * is unwrapped — through `driverProfileOf` rather than `firstOf`, because `driver_id` is unique
 * only where `admin_id IS NULL` and an admin's test profile in that array would put the WRONG
 * PERSON'S NAME on the deposit agreement. `admin_id` is selected so the pick has something to read.
 */

import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

import { toContractDeposits, type ContractDeposit, type RawContractDepositRow } from './depositShapes';

/**
 * The pure half of this read — the row shape, the embed unwrapping and the two derivations the
 * board and the money strip take off it — lives in `depositShapes.ts`, where a node-env test can
 * import it without dragging the supabase client in. It is re-exported here so callers have one
 * import for "the contract's deposits".
 */
export {
    depositSubjectsFor,
    depositTotals,
    firstOf,
    toContractDeposits,
} from './depositShapes';
export type {
    ContractDeposit,
    DepositAgreementLink,
    Embedded,
    RawContractDepositRow,
} from './depositShapes';

/** Prefix form — what a writer invalidates when it does not know the contract. */
export const CONTRACT_DEPOSITS_BY_CONTRACT_KEY = ['contract-deposits', 'by-contract'] as const;

export const contractDepositsKey = (contractId: number) =>
    ['contract-deposits', 'by-contract', contractId] as const;

const CONTRACT_DEPOSITS_SELECT = `
    id,
    created_at,
    contract_id,
    transaction_id,
    remarks,
    transactions ( id, amount, currency, transaction_at, description ),
    contract_deposit_refunds ( id, transactions ( amount ) ),
    contract_deposit_agreements ( id, signed_at, agreement_url, origin ),
    contracts ( id, drivers ( id, profiles ( id, admin_id, first_name, middle_name, last_name, phone ) ) )
`;

/** Deposits linked to this contract, oldest payment first. */
export async function fetchContractDeposits(contractId: number): Promise<ContractDeposit[]> {
    if (!contractId) return [];

    const { data, error } = await supabase
        .from('contract_deposits')
        .select(CONTRACT_DEPOSITS_SELECT)
        .eq('contract_id', contractId)
        .order('id', { ascending: true });
    if (error) throw error;

    return toContractDeposits((data ?? []) as unknown as RawContractDepositRow[]);
}

export function useContractDeposits(contractId: number) {
    return useQuery({
        queryKey: contractDepositsKey(contractId),
        queryFn: () => fetchContractDeposits(contractId),
        enabled: !!contractId,
    });
}

/**
 * Record a payment that is not in the pool, straight onto this contract.
 *
 * `CreateDepositSheet` OMITS `contract_id` DELIBERATELY — it is the deposits page's own control,
 * where a payment is recorded before anybody knows which contract it belongs to. Recorded from the
 * setup canvas the answer is already known, so it is written, and the payment never passes through
 * the unassigned queue at all.
 */
export async function createContractDeposit({
    contractId,
    transactionId,
    remarks,
}: {
    contractId: number;
    transactionId: number | null;
    remarks: string;
}): Promise<void> {
    if (!transactionId) throw new Error('Select the bank transaction this deposit was paid on.');

    const { error } = await supabase
        .from('contract_deposits')
        .insert({
            contract_id: contractId,
            transaction_id: transactionId,
            remarks: remarks.trim() || null,
        });
    if (error) throw error;
}
